import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import LogRocket from 'logrocket';
import { nanoid } from 'nanoid';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  createInstantSurvey,
  DEFAULT_GROUP_NAME,
  MAINTENANCE_MESSAGE,
  QUESTION_TYPES,
  readInstantSurvey,
  readInstantSurveyType,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  updateInstantSurvey,
} from '@api/instant_survey';
import { createAtlasSurvey, readSurvey, readSurveyType, surveyStatus } from '@api/surveys';
import { readClientSubscription } from '@api/client';

import { GATES } from '@constants';
import { updateUserCreditsRemaining } from '@redux/slices/user';
import {
  Button,
  CheckSquareIcon,
  Error,
  LoaderSkeleton,
  MaintenanceView,
  ModalV2 as Modal,
  ShieldTipIcon,
  toast,
  useAutoOpenAfterDelay,
} from '@utilities';

import Column from '../../layouts/Column';
import Confirmation from './components/Confirmation';
import ModalUnsavedChanges from './components/ModalUnsavedChanges';
import Panelists from './components/Panelists';
import Questionnaire from './components/Questionnaire';
import Review from './components/Review';
import SurveyNetworkError from '../../components/SurveyNetworkError';

import { payWithCompletes, payWithCredits } from '../Checkout/utilities/constants';

import {
  AUTO_SAVE_DEBOUNCE_MS,
  MAX_AUTO_SAVE_RETRIES,
  SURVEY_STEPS,
  SURVEY_TEMPORARY_TITLE,
} from './utilities/constants';
import { SurveyContext } from './utilities/context';
import {
  generateTemporarySurveyTitle,
  getCurrentQuestionnairePrice,
  getModalConfig,
  getQuestionCost,
  getQuestionsCost,
  getQuotaLimit,
  isQuestionLogicComplete,
} from './utilities/helpers';

import { ReactComponent as NumeratorLogo } from '../../components/Header/assets/data-wave-logo.svg';

import styles from './_index.module.scss';

const CreateInstantSurvey = () => {
  const [activeStep, setActiveStep] = useState(SURVEY_STEPS.PANELISTS);
  const [autoSaveError, setAutoSaveError] = useState(null);
  const [error, setError] = useState();
  const [hasLaunched, setHasLaunched] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [insightsDocument, setInsightsDocument] = useState();
  const [insightsDocumentType, setInsightsDocumentType] = useState();
  const [instantSurveyType, setInstantSurveyType] = useState();
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isSaveAsDraftOpen, setIsSaveAsDraftOpen] = useState(false);
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [jobId, setJobId] = useState('');
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const [showAIGenerationModal, setShowAIGenerationModal] = useState(false);
  const [showSurveyTemplateModal, setShowSurveyTemplateModal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [surveyGoals, setSurveyGoals] = useState('');

  const userData = useSelector((state) => state?.user?.data);
  const isSubscribed = !!userData?.clientInfo?.subscriptions?.length;

  const [launchMethod, setLaunchMethodState] = useState(payWithCredits.key);

  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const isAutoSaveGateEnabled = client.checkGate(GATES.INSTANT_SURVEY_AUTO_SAVE);
  const isInstantSurveyMaintenance = client.checkGate(GATES.INSTANT_SURVEY_MAINTENANCE);

  const { instant_survey_type_id: instantSurveyTypeId } = useParams();

  const { panel } = userData || {};
  const defaultResponseCount = getQuotaLimit(instantSurveyTypeId);

  const [survey, setSurvey] = useState({
    country: panel?.abbrev?.toLowerCase(),
    groups: [
      {
        clientId: nanoid(),
        genRepSampleType: null,
        id: nanoid(),
        name: DEFAULT_GROUP_NAME,
        promptAnswers: [],
        quotaLimit: defaultResponseCount,
      },
    ],
    id: null,
    instantSurveyType: instantSurveyTypeId,
    questionLogic: {},
    questions: [],
    researchObjectives: '',
    surveyTemplateId: null,
    title: '',
  });
  const autoSaveTimeoutRef = useRef(null);
  const autoSaveAbortControllerRef = useRef(null);
  const autoSaveRetryCountRef = useRef(0);
  const autoSaveRetryTimeoutRef = useRef(null);
  const lastPersistedTitleRef = useRef('');
  const persistSurveyDraftPromiseRef = useRef(null);
  const isComponentMountedRef = useRef(false);
  const insightsDocumentRef = useRef();
  const hasUnsavedChangesRef = useRef(false);
  const surveyRef = useRef(null);
  surveyRef.current = survey;

  // versioning so we don’t clear dirty for stale saves
  const surveyVersionRef = useRef(0);

  const assignClientIds = useCallback((nextGroups = [], prevGroups = []) => {
    const clientIdByServerId = new Map(
      prevGroups
        .filter(({ clientId, id }) => id && clientId)
        .map(({ clientId, id }) => [id, clientId])
    );

    return nextGroups.map((group, index) => {
      const clientId =
        clientIdByServerId.get(group.id) ??
        group.clientId ??
        prevGroups[index]?.clientId ??
        nanoid();
      return { ...group, clientId };
    });
  }, []);

  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);
  const activeQuestions = survey.questions.filter(({ _delete }) => !_delete);

  const isMonadicConceptTest =
    instantSurveyType?.id === 'monadic-concept-test-v3-instant-survey-type';
  const isTURF = instantSurveyType?.id === 'turf-v3-instant-survey-type';
  const isNewItemSurvey = instantSurveyType?.id === 'new-item-v3-instant-survey-type';
  const isVideoSurvey = instantSurveyType?.id === 'video-survey-v3-instant-survey-type';

  const currentQuestionCount = getQuestionsCost(activeQuestions);
  const currentQuestionnairePrice = useMemo(
    () =>
      getCurrentQuestionnairePrice({
        questionnairePricing: insightsDocumentType?.questionnairePricing,
        survey,
      }),
    [survey, insightsDocumentType?.questionnairePricing]
  );

  const questionDisplayIndexById = useMemo(() => {
    let currentIndex = 1;
    return survey.questions
      .filter(({ _delete }) => !_delete)
      .reduce((acc, question) => {
        if (question.type === QUESTION_TYPES.INSTRUCTIONS.value) {
          acc[question.id] = 'T';
          return acc;
        }
        const questionCost = getQuestionCost(question);
        acc[question.id] =
          `${currentIndex}${questionCost > 1 ? `-${currentIndex + questionCost - 1}` : ''}`;
        currentIndex += questionCost;
        return acc;
      }, {});
  }, [survey.questions]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const insightsId = searchParams.get('id');

  const infoModalSurveyType = useMemo(() => {
    if (isMonadicConceptTest) {
      return 'monadic';
    } else if (isTURF) {
      return 'turf';
    } else if (isNewItemSurvey) {
      return 'new-item';
    }
  }, [isMonadicConceptTest, isTURF, isNewItemSurvey]);

  const infoModalConfig =
    !insightsId && infoModalSurveyType ? getModalConfig({ surveyType: infoModalSurveyType }) : null;

  const { close: closeInfoModal, isOpen: isInfoModalOpen } = useAutoOpenAfterDelay(
    500,
    infoModalSurveyType
  );

  const shouldBlock = useCallback(() => {
    if (!isAutoSaveGateEnabled) return false;
    return !(hasLaunched || isInstantSurveyMaintenance || isSavingAsDraft) && hasUnsavedChanges;
  }, [
    hasLaunched,
    hasUnsavedChanges,
    isAutoSaveGateEnabled,
    isInstantSurveyMaintenance,
    isSavingAsDraft,
  ]);
  const blocker = useBlocker(shouldBlock);
  const canAutoSave = useMemo(() => {
    return (
      isAutoSaveGateEnabled &&
      !isInstantSurveyMaintenance &&
      !isLoadingSurvey &&
      !isStatsigLoading &&
      !hasLaunched
    );
  }, [
    isAutoSaveGateEnabled,
    isInstantSurveyMaintenance,
    isLoadingSurvey,
    isStatsigLoading,
    hasLaunched,
  ]);

  const syncInsightsDocument = useCallback((document) => {
    insightsDocumentRef.current = document;
    if (!isComponentMountedRef.current) return;
    setInsightsDocument(document);
  }, []);

  useEffect(() => {
    isComponentMountedRef.current = true;

    return () => {
      // Flag pending async callbacks so they skip setState once unmounted
      isComponentMountedRef.current = false;
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (autoSaveRetryTimeoutRef.current) {
        clearTimeout(autoSaveRetryTimeoutRef.current);
      }
      // Abort any pending auto-save request
      autoSaveAbortControllerRef.current?.abort();
    };
  }, []);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!survey.id || !insightsDocument?.id || insightsId) return;

    const nextParams = new URLSearchParams(searchParams);
    const atlasId = String(insightsDocument.id);
    const instantSurveyId = String(survey.id);
    const needsIdUpdate = nextParams.get('id') !== atlasId;
    const needsRefUpdate = nextParams.get('ref') !== instantSurveyId;

    if (!needsIdUpdate && !needsRefUpdate) return;

    nextParams.set('id', atlasId);
    nextParams.set('ref', instantSurveyId);
    setSearchParams(nextParams, { replace: true });
  }, [insightsDocument?.id, insightsId, searchParams, setSearchParams, survey.id]);

  useEffect(() => {
    if (!isStatsigLoading && !isInstantSurveyMaintenance) {
      fetchSurveyData();
      if (isSubscribed) {
        fetchSubscription();
      }
    }
  }, [isStatsigLoading]);

  const fetchSubscription = async () => {
    setSubscriptionError(null);
    setIsLoadingSubscription(true);

    try {
      const subscriptionData = await readClientSubscription();
      setSubscription(subscriptionData[0]);
      if (subscriptionData[0]?.hasStarted) {
        setLaunchMethod(payWithCompletes.key, { markDirty: false });
      }
    } catch (err) {
      setSubscriptionError(err);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const clearAutoSaveTimeout = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  const clearAutoSaveRetryTimeout = useCallback(() => {
    if (autoSaveRetryTimeoutRef.current) {
      clearTimeout(autoSaveRetryTimeoutRef.current);
      autoSaveRetryTimeoutRef.current = null;
    }
  }, []);

  const fetchSurveyData = async () => {
    try {
      const surveyRequests = [
        readInstantSurveyType({ id: instantSurveyTypeId }),
        readSurveyType({ documentId: instantSurveyTypeId }),
      ];
      const instantSurveyId = searchParams.get('ref');
      const isModal = searchParams.get('modal');
      const hasRefOnly = instantSurveyId && !insightsId;

      if (insightsId && instantSurveyId) {
        surveyRequests.push(
          readInstantSurvey({ id: instantSurveyId }),
          readSurvey({ id: insightsId })
        );
      }
      if ((instantSurveyId && isModal) || hasRefOnly) {
        surveyRequests.push(readInstantSurvey({ id: instantSurveyId }));
      }

      const [
        instantSurveyTypeResponse,
        insightsDocumentTypeResponse,
        instantSurveyDraftResponse,
        insightsDocumentResponse,
      ] = await Promise.all(surveyRequests);

      const insightsDocumentStatus = insightsDocumentResponse?.status;
      if (insightsDocumentStatus && insightsDocumentStatus !== surveyStatus.DRAFT) {
        toast(
          'The survey you are looking for is not available to edit. Please search for it in your Workspace.'
        );
        navigate('/dashboard/workspace/surveys?filters=');
        return;
      }

      setInstantSurveyType(instantSurveyTypeResponse);
      setInsightsDocumentType({
        ...insightsDocumentTypeResponse,
        samplePricing: insightsDocumentTypeResponse.samplePricing.filter(
          ({ isAskWhy, type }) => !isAskWhy && type === 'CREDITS'
        ),
      });

      if (insightsId && instantSurveyId) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        // if (hasQuestionLogicError) {
        //   toast(
        //     'There was an error while rebuilding your questionnaire logic. Please confirm your selections before continuing.',
        //     { status: 'error' }
        //   );
        //   setHadQuestionLogicError(true);
        // }

        syncInsightsDocument(insightsDocumentResponse);
        const savedLaunchMethod = insightsDocumentResponse?.paymentType;

        if (savedLaunchMethod) {
          const launchMethodValue =
            savedLaunchMethod === payWithCompletes.key && isSubscribed
              ? payWithCompletes.key
              : payWithCredits.key;

          setLaunchMethod(launchMethodValue, { markDirty: false });
        }

        lastPersistedTitleRef.current = surveyData.title || '';
        setSurvey((prevSurvey) => ({
          ...surveyData,
          groups: assignClientIds(surveyData.groups, prevSurvey.groups),
        }));
      } else if ((instantSurveyId && isModal) || hasRefOnly) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        lastPersistedTitleRef.current = surveyData.title || '';
        setSurvey((prevSurvey) => ({
          ...surveyData,
          groups: assignClientIds(surveyData.groups, prevSurvey.groups),
          title: surveyData.title === SURVEY_TEMPORARY_TITLE ? '' : surveyData.title,
        }));
      }

      // Reset dirty state BEFORE enabling auto-save
      setHasUnsavedChanges(false);
      hasUnsavedChangesRef.current = false;
      surveyVersionRef.current = 0;
      autoSaveRetryCountRef.current = 0;

      clearAutoSaveTimeout();
      clearAutoSaveRetryTimeout();

      setIsLoadingSurvey(false);
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const handleSurveyPublish = ({ userCreditsRemaining }) => {
    LogRocket.track('InstantSurvey_Published', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyTypeId,
    });
    dispatch(updateUserCreditsRemaining({ userCreditsRemaining }));
    setHasLaunched(true);
  };

  const saveInstantSurvey = useCallback(
    async ({ signal } = {}) => {
      const requestVersion = surveyVersionRef.current;
      const surveySnapshot = surveyRef.current;

      const { id, title, ...surveyData } = surveySnapshot;
      const cleanedTitle = title?.trim() || '';
      const shouldUseAutoTitle = !id && !cleanedTitle;
      const resolvedTitle =
        shouldUseAutoTitle || !cleanedTitle
          ? lastPersistedTitleRef.current || generateTemporarySurveyTitle()
          : cleanedTitle;

      let response;
      if (!id) {
        response = await createInstantSurvey({
          instantSurvey: {
            ...surveyData,
            status: 'DRAFT',
            title: resolvedTitle,
          },
          signal,
        });
      } else {
        response = await updateInstantSurvey({
          instantSurvey: {
            ...surveyData,
            id,
            title: resolvedTitle,
          },
          signal,
        });
      }

      const { title: savedSurveyTitle, ...surveyResponse } = response;

      // stale-response guard: don’t overwrite newer edits
      if (requestVersion < surveyVersionRef.current) {
        return response;
      }

      lastPersistedTitleRef.current = savedSurveyTitle;

      setSurvey((prevSurvey) => {
        const nextGroups =
          surveyResponse.groups !== undefined
            ? assignClientIds(surveyResponse.groups, prevSurvey.groups)
            : prevSurvey.groups;
        const nextQuestionLogic =
          surveyResponse.questionLogic !== undefined
            ? surveyResponse.questionLogic
            : prevSurvey.questionLogic;
        const nextQuestions =
          Array.isArray(surveyResponse.questions) && surveyResponse.questions.length > 0
            ? surveyResponse.questions
            : prevSurvey.questions;

        return {
          ...prevSurvey,
          ...surveyResponse,
          groups: nextGroups,
          questionLogic: nextQuestionLogic,
          questions: nextQuestions,
          title: savedSurveyTitle === SURVEY_TEMPORARY_TITLE ? '' : savedSurveyTitle,
        };
      });

      return response;
    },
    [assignClientIds]
  );

  const persistSurveyDraft = useCallback(
    async ({ reason = 'auto', signal, targetVersion = surveyVersionRef.current } = {}) => {
      const isManualSave = reason !== 'auto';
      clearAutoSaveRetryTimeout();

      if (!isManualSave) {
        const noPendingChanges =
          !hasUnsavedChangesRef.current && targetVersion === surveyVersionRef.current;
        if (!canAutoSave || isSavingAsDraft || noPendingChanges) {
          return null;
        }
      } else {
        clearAutoSaveTimeout();
      }

      if (persistSurveyDraftPromiseRef.current) {
        if (!isManualSave) {
          return persistSurveyDraftPromiseRef.current.finally(() => {
            if (!isComponentMountedRef.current) {
              return null;
            }

            const hasPendingChanges =
              hasUnsavedChangesRef.current || surveyVersionRef.current !== targetVersion;

            if (!hasPendingChanges) {
              return null;
            }

            return persistSurveyDraft({
              reason: 'auto',
              targetVersion: surveyVersionRef.current,
            });
          });
        }
        await persistSurveyDraftPromiseRef.current;
      }

      const persistPromise = (async () => {
        let requestSignal = signal;

        try {
          if (!isManualSave) {
            autoSaveAbortControllerRef.current?.abort();
            autoSaveAbortControllerRef.current = new AbortController();
            requestSignal = autoSaveAbortControllerRef.current.signal;
            if (isComponentMountedRef.current) setIsAutoSaving(true);
          }

          if (isComponentMountedRef.current) setAutoSaveError(null);

          const instantSurveyResponse = await saveInstantSurvey({ signal: requestSignal });
          const atlasSurveyResponse = await createAtlasSurvey({
            atlasInstantSurveyRef: instantSurveyResponse.id,
            id: insightsDocumentRef.current?.id,
            paymentMethod: launchMethod,
            status: 'draft',
            title:
              instantSurveyResponse.title ||
              lastPersistedTitleRef.current ||
              generateTemporarySurveyTitle(),
          });

          if (atlasSurveyResponse?.instantSurvey) {
            syncInsightsDocument(atlasSurveyResponse.instantSurvey);
          }

          const isLatestVersion = targetVersion === surveyVersionRef.current;

          if (isLatestVersion) {
            hasUnsavedChangesRef.current = false;
            autoSaveRetryCountRef.current = 0;

            if (isComponentMountedRef.current) {
              setHasUnsavedChanges(false);
              if (!isManualSave) setLastAutoSavedAt(new Date());
            }
          }

          return { atlasSurveyResponse, instantSurveyResponse };
        } catch (error) {
          // Silently ignore aborted requests
          if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
            return null;
          }

          if (isManualSave) {
            throw error;
          }

          if (autoSaveRetryCountRef.current < MAX_AUTO_SAVE_RETRIES) {
            autoSaveRetryCountRef.current += 1;

            clearAutoSaveRetryTimeout();
            autoSaveRetryTimeoutRef.current = setTimeout(
              () => {
                autoSaveRetryTimeoutRef.current = null;
                if (isComponentMountedRef.current) {
                  persistSurveyDraft({
                    reason: 'auto',
                    targetVersion,
                  });
                }
              },
              1000 * Math.pow(2, autoSaveRetryCountRef.current)
            );

            return null;
          }

          autoSaveRetryCountRef.current = 0;
          if (isComponentMountedRef.current) setAutoSaveError(error);
          console.error('Auto-save error:', error);
          return null;
        } finally {
          if (!isManualSave && isComponentMountedRef.current) {
            setIsAutoSaving(false);
            if (autoSaveAbortControllerRef.current?.signal === requestSignal) {
              autoSaveAbortControllerRef.current = null;
            }
          }
          if (persistSurveyDraftPromiseRef.current === persistPromise) {
            persistSurveyDraftPromiseRef.current = null;
          }
        }
      })();

      persistSurveyDraftPromiseRef.current = persistPromise;
      return persistPromise;
    },
    [
      canAutoSave,
      clearAutoSaveRetryTimeout,
      clearAutoSaveTimeout,
      isSavingAsDraft,
      launchMethod,
      saveInstantSurvey,
      syncInsightsDocument,
    ]
  );

  const scheduleAutoSave = useCallback(
    ({ targetVersion = surveyVersionRef.current } = {}) => {
      if (!canAutoSave) return;

      clearAutoSaveTimeout();

      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTimeoutRef.current = null;

        const hasChanges =
          hasUnsavedChangesRef.current || targetVersion !== surveyVersionRef.current;

        if (!isComponentMountedRef.current || !hasChanges) return;

        persistSurveyDraft({ reason: 'auto', targetVersion });
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [canAutoSave, clearAutoSaveTimeout, persistSurveyDraft]
  );

  const setLaunchMethod = useCallback(
    (nextLaunchMethod, { markDirty = true } = {}) => {
      const didChange = launchMethod !== nextLaunchMethod;

      if (didChange) {
        setLaunchMethodState(nextLaunchMethod);
      }

      if (!markDirty || !didChange) return;

      surveyVersionRef.current += 1;
      const targetVersion = surveyVersionRef.current;

      setHasUnsavedChanges(true);
      hasUnsavedChangesRef.current = true;

      scheduleAutoSave({ targetVersion });
    },
    [launchMethod, scheduleAutoSave]
  );

  const updateSurvey = useCallback(
    (fields, { markDirty = false } = {}) => {
      let nextQuestionLogicIsComplete = true;
      let isQuestionLogicUpdate = false;

      setSurvey((prev) => {
        const next = { ...prev, ...fields };
        surveyRef.current = next;

        if (fields.questionLogic !== undefined) {
          isQuestionLogicUpdate = true;
          nextQuestionLogicIsComplete = isQuestionLogicComplete(next.questionLogic);
        }

        return next;
      });

      if (!markDirty) return;

      surveyVersionRef.current += 1;
      const targetVersion = surveyVersionRef.current;

      setHasUnsavedChanges(true);
      hasUnsavedChangesRef.current = true;

      // don't auto-save while logic is mid-build (prevents server overwrite)
      if (isQuestionLogicUpdate && !nextQuestionLogicIsComplete) {
        clearAutoSaveTimeout(); // cancel any pending save that would wipe in-progress logic
        return;
      }

      scheduleAutoSave({ targetVersion });
    },
    [clearAutoSaveTimeout, scheduleAutoSave]
  );

  const handleBlockedNavigation = useCallback(async () => {
    try {
      await persistSurveyDraft({ reason: 'navigation', targetVersion: surveyVersionRef.current });
    } catch (err) {
      console.error('Failed to save before navigation:', err);
    } finally {
      blocker.proceed();
    }
  }, [blocker, persistSurveyDraft]);

  if (isInstantSurveyMaintenance) {
    return <MaintenanceView heading={MAINTENANCE_MESSAGE} logo={<NumeratorLogo />} />;
  }
  if (error) {
    return error?.code === 'ERR_NETWORK' ? (
      <SurveyNetworkError />
    ) : (
      <Error status={error?.response?.status} />
    );
  }

  if (hasLaunched) return <Confirmation />;
  if (isLoadingSurvey || isStatsigLoading) {
    return (
      <LoaderSkeleton height={450}>
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="0" />
        <rect height="130" rx="4" ry="4" width="1333" x="0" y="60" />
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="210" />
        <rect height="20" rx="4" ry="4" width="1333" x="0" y="270" />
        <rect height="20" rx="2" ry="2" width="175" x="0" y="310" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="350" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="375" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="400" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="425" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="450" />
      </LoaderSkeleton>
    );
  }

  return (
    <SurveyContext.Provider
      value={{
        activeGroups,
        activeQuestions,
        activeStep,
        autoSaveError,
        currentQuestionCount,
        currentQuestionnairePrice,
        hasUnsavedChanges,
        insightsDocument,
        insightsDocumentType,
        instantSurveyType,
        isAutoSaving,
        isLoadingSubscription,
        isResearchObjectivesTooLong:
          survey.researchObjectives?.length > RESEARCH_OBJECTIVES_MAX_LENGTH,
        isSaveAsDraftOpen,
        isSavingAsDraft,
        isSubscribed,
        jobId,
        lastAutoSavedAt,
        launchMethod,
        maxQuestions: isVideoSurvey ? 1 : insightsDocumentType.maxQuestions,
        persistSurveyDraft,
        questionDisplayIndexById,
        saveInstantSurvey,
        setActiveStep,
        setIsSaveAsDraftOpen,
        setIsSavingAsDraft,
        setJobId,
        setLaunchMethod,
        setShowAIGenerationModal,
        setShowSurveyTemplateModal,
        setSurveyGoals,
        showAIGenerationModal,
        showSurveyTemplateModal,
        subscription,
        subscriptionError,
        survey,
        surveyGoals,
        updateSurvey,
      }}
    >
      {activeStep === SURVEY_STEPS.PANELISTS && (
        <Panelists
          defaultResponseCount={defaultResponseCount}
          onNext={() => setActiveStep(SURVEY_STEPS.QUESTIONNAIRE)}
        />
      )}
      {activeStep === SURVEY_STEPS.QUESTIONNAIRE && (
        <Questionnaire
          onNext={() => setActiveStep(SURVEY_STEPS.REVIEW)}
          onPrevious={() => setActiveStep(SURVEY_STEPS.PANELISTS)}
        />
      )}
      {activeStep === SURVEY_STEPS.REVIEW && (
        <Review onPublish={handleSurveyPublish} setActiveStep={setActiveStep} />
      )}

      {blocker?.state === 'blocked' && (
        <ModalUnsavedChanges onClose={blocker.reset} onContinue={handleBlockedNavigation} />
      )}

      {infoModalConfig && (
        <Modal
          buttons={[<Button key="close" onClick={closeInfoModal} text="Close" variant="primary" />]}
          className={styles['create-instant-survey-info-modal']}
          isActive={isInfoModalOpen}
          onClose={closeInfoModal}
          title={infoModalConfig.title}
        >
          <p>
            {infoModalConfig.description}{' '}
            <a href={infoModalConfig.link} rel="noreferrer" target="_blank">
              Learn more
            </a>
          </p>
          <h3>
            <ShieldTipIcon />
            Setup
          </h3>
          <ol>
            {infoModalConfig.steps.map((step) => (
              <li key={step}>
                <CheckSquareIcon />
                {step}
              </li>
            ))}
          </ol>
        </Modal>
      )}
    </SurveyContext.Provider>
  );
};

const CreateInstantSurveyLayout = () => (
  <Column>
    <CreateInstantSurvey />
  </Column>
);

export default CreateInstantSurveyLayout;
